import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { useEnquire } from "@/components/ContactModal";
import { Eyebrow, FramedPhoto, KineticLines, MarqueeRibbon, ParallaxImage, Reveal } from "@/components/motion";

const Home = () => {
  const { t } = useLang();
  const { setOpen } = useEnquire();

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
        <ParallaxImage src="/images/ocm-hero.jpg" alt="Otto Cilindri Manuale" className="absolute inset-0 h-full" imgClassName="w-[118%] max-w-none" shiftX="5%" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E14]/70 via-[#0A0E14]/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E14]/60 via-transparent to-[#0A0E14]/15" />
        <div className="relative mx-auto w-full max-w-[1440px] px-6 pb-20 pt-28 lg:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
          >
            <Eyebrow>{t.hero.eyebrow}</Eyebrow>
          </motion.p>
          <h1 className="mt-8">
            <KineticLines
              lines={t.hero.lines}
              delay={0.35}
              className="text-5xl leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
              lineClassName={["font-heritage italic", "font-tech uppercase tracking-tight"]}
            />
          </h1>
          <motion.p
            data-testid="hero-sub"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl font-heritage text-2xl italic leading-snug text-neutral-100 sm:text-3xl"
          >
            {t.hero.sub}
          </motion.p>
        </div>
        <div aria-hidden="true" className="absolute bottom-8 right-8 hidden items-center gap-4 md:flex">
          <span className="font-tech text-[10px] uppercase tracking-[0.4em] text-white/50">{t.hero.scroll}</span>
          <motion.span
            className="block h-16 w-px origin-top bg-white/40"
            animate={{ scaleY: [0, 1, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="py-16">
        <div className="mx-auto grid max-w-[1440px] gap-16 px-6 md:grid-cols-12 lg:px-12">
          <div className="md:col-span-4">
            <Reveal>
              <Eyebrow>{t.manifesto.eyebrow}</Eyebrow>
              <div className="mt-8 h-px w-24 bg-[#4A7BA6]" />
            </Reveal>
          </div>
          <div className="flex flex-col gap-12 md:col-span-8">
            {t.manifesto.chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1} testId={`manifesto-chapter-${c.n}`}>
                <div className="flex items-baseline gap-6">
                  <span className="font-tech text-sm tracking-[0.3em] text-[#4A7BA6]">{c.n}</span>
                  <h2 className="font-tech text-sm uppercase tracking-[0.4em] text-white/50">{c.title}</h2>
                </div>
                <p className="mt-8 font-heritage text-3xl leading-snug text-white/90 sm:text-4xl lg:text-5xl">
                  {c.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MarqueeRibbon items={t.marquee} />

      {/* COLLAGE BAND */}
      <section data-testid="ocm-collage-section" className="mx-auto max-w-[1440px] px-6 py-14 lg:px-12">
        <Reveal testId="ocm-collage">
          <img src="/images/ocm2-side.jpg" alt="Otto Cilindri Manuale" className="w-full" />
        </Reveal>
      </section>

      {/* JANO PORTRAITS */}
      <section data-testid="jano-section" className="py-16">
        <div className="mx-auto grid max-w-[1440px] items-start gap-12 px-6 md:grid-cols-12 lg:px-12">
          <div className="md:col-span-5">
            <Reveal testId="jano-portrait-primary">
              <FramedPhoto src="/images/jano-group-1955.jpg" alt="Vittorio Jano with Alberto Ascari, Luigi Villoresi and Eugenio Castellotti" caption={t.jano.cap1} />
            </Reveal>
          </div>
          <div className="md:col-span-3">
            <Reveal delay={0.15} testId="jano-portrait-secondary">
              <FramedPhoto src="/images/jano-young-car.jpg" alt="Vittorio Jano with a Grand Prix car" caption={t.jano.cap2} imgClassName="aspect-[3/4]" />
            </Reveal>
          </div>
          <div className="md:col-span-4 md:pt-24">
            <Reveal delay={0.1}>
              <Eyebrow>{t.jano.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-heritage text-5xl italic tracking-tight">{t.jano.title}</h2>
              <blockquote className="mt-10 border-l-2 border-[#4A7BA6] pl-6 font-heritage text-2xl italic leading-snug text-white/90">
                {t.jano.quote}
              </blockquote>
              <p className="mt-10 text-base leading-relaxed text-neutral-400">{t.jano.text}</p>
              <Link
                to="/biography"
                data-testid="jano-biography-link"
                className="group mt-10 inline-flex items-center gap-3 font-tech text-sm uppercase tracking-[0.25em] text-white transition-colors hover:text-[#4A7BA6]"
              >
                {t.jano.cta}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VJ 8C TEASER */}
      <section data-testid="vj8c-teaser-section" className="py-16">
        <div className="mx-auto grid max-w-[1440px] items-center gap-16 px-6 md:grid-cols-12 lg:px-12">
          <div className="md:col-span-7">
            <Reveal testId="vj8c-teaser-image">
              <img src="/images/ocm-teaser-3.jpg" alt="Otto Cilindri Manuale" className="w-full border border-white/10" />
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <Eyebrow>{t.teaser.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-tech text-4xl font-bold uppercase leading-tight tracking-tight sm:text-5xl">
                {t.teaser.title}
              </h2>
              <p className="mt-8 max-w-md text-base leading-relaxed text-neutral-400">{t.teaser.text}</p>
              <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-8" data-testid="vj8c-teaser-stats">
                {t.teaser.stats.map((s) => (
                  <div key={s.label}>
                    <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">{s.label}</p>
                    <p className="mt-2 font-tech text-lg uppercase tracking-wide text-white">{s.value}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/otto-cilindri-manuale"
                data-testid="vj8c-teaser-cta"
                className="group mt-12 inline-flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 font-tech text-sm uppercase tracking-[0.25em] transition-colors duration-300 hover:border-[#4A7BA6] hover:bg-[#4A7BA6]"
              >
                {t.teaser.cta}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BI VENTI TEASER */}
      <section data-testid="biventi-teaser-section" className="border-t border-white/10 py-16">
        <div className="mx-auto grid max-w-[1440px] items-center gap-16 px-6 md:grid-cols-12 lg:px-12">
          <div className="md:col-span-7">
            <Reveal testId="biventi-teaser-image">
              <img src="/images/biventi-hero.jpg" alt="Bi Venti" className="w-full border border-white/10" />
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <Eyebrow>{t.teaser2.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-tech text-4xl font-bold uppercase leading-tight tracking-tight sm:text-5xl">
                {t.teaser2.title}
              </h2>
              <p className="mt-8 max-w-md text-base leading-relaxed text-neutral-400">{t.teaser2.text}</p>
              <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-8" data-testid="biventi-teaser-stats">
                {t.teaser2.stats.map((s) => (
                  <div key={s.label}>
                    <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">{s.label}</p>
                    <p className="mt-2 font-tech text-lg uppercase tracking-wide text-white">{s.value}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/bi-venti"
                data-testid="biventi-teaser-cta"
                className="group mt-12 inline-flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 font-tech text-sm uppercase tracking-[0.25em] transition-colors duration-300 hover:border-[#4A7BA6] hover:bg-[#4A7BA6]"
              >
                {t.teaser2.cta}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section data-testid="contact-cta-section" className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <Reveal>
            <Eyebrow>{t.contact.eyebrow}</Eyebrow>
            <h2 className="mt-8 max-w-3xl font-heritage text-4xl italic leading-tight text-white/90 sm:text-6xl">
              {t.contact.title}
            </h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400">{t.contact.text}</p>
            <button
              data-testid="contact-cta-button"
              onClick={() => setOpen(true)}
              className="mt-12 rounded-full bg-[#4A7BA6] px-10 py-4 font-tech text-sm uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
              {t.contact.cta}
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
